/**
 * CreditSpendContext — #77 app-wide credit-UX (generalizes LongevityPage.spend()).
 *
 * Provides:
 *   costOf(apiType)  → the credit cost for an action (from /api/profile/ai-usage), or null if unknown
 *   spend(apiType, title, run) → pre-action confirm for ≥4-credit OR low-balance actions, else run now
 *   creditsLeft      → daily.remaining + purchased (or null while loading)
 *   refreshBalance() → re-pull the balance (also auto-refreshes on tab focus)
 *
 * Costs are ALWAYS read live from the backend (AI_CREDIT_WEIGHTS via /ai-usage) — never hardcoded —
 * so the cost-chips can't drift from the source of truth. Threshold matches the shipped Longevity
 * pattern: cost >= 4 OR creditsLeft <= cost + 2 → confirm first.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useFetchWithAuth } from '../hooks/useFetchWithAuth';

const CreditSpendContext = createContext(null);

export function useCreditSpend() {
  const ctx = useContext(CreditSpendContext);
  // Defensive no-op fallback if a consumer renders outside the provider: never block the action.
  if (!ctx) return { costOf: () => null, spend: (_a, _t, run) => run && run(), refreshBalance: () => {}, creditsLeft: null };
  return ctx;
}

export function CreditSpendProvider({ children }) {
  const { fetchWithAuth, API_BASE, token } = useFetchWithAuth();
  const [balance, setBalance] = useState(null);   // { daily: { remaining }, purchased_credits }
  const [costs, setCosts] = useState({});          // { api_type: credit_cost }
  const [confirm, setConfirm] = useState(null);    // { title, cost, onRun }

  const creditsLeft = balance?.daily
    ? (balance.daily.remaining ?? 0) + (balance.purchased_credits ?? 0)
    : null;

  const refreshBalance = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/billing/credits/balance`);
      if (res.ok) setBalance(await res.json());
    } catch (_) { /* non-fatal — never block on balance */ }
  }, [fetchWithAuth, API_BASE]);

  const loadCosts = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/profile/ai-usage`);
      if (res.ok) {
        const d = await res.json();
        const map = {};
        Object.entries(d.available_actions || {}).forEach(([k, v]) => { map[k] = v.credit_cost; });
        setCosts(map);
      }
    } catch (_) { /* non-fatal */ }
  }, [fetchWithAuth, API_BASE]);

  useEffect(() => {
    if (!token) return;
    refreshBalance();
    loadCosts();
  }, [token, refreshBalance, loadCosts]);

  // Event-driven refresh (matches App's no-blind-poll pattern) — keeps balance current for confirms.
  useEffect(() => {
    const onActive = () => { if (document.visibilityState === 'visible') refreshBalance(); };
    window.addEventListener('focus', onActive);
    document.addEventListener('visibilitychange', onActive);
    return () => {
      window.removeEventListener('focus', onActive);
      document.removeEventListener('visibilitychange', onActive);
    };
  }, [refreshBalance]);

  const costOf = useCallback((apiType) => (apiType in costs ? costs[apiType] : null), [costs]);

  const spend = useCallback((apiType, title, run) => {
    const cost = (apiType in costs) ? costs[apiType] : 0;
    if (!cost || cost <= 0) { run && run(); return; }            // free/unknown → run immediately
    const lowBalance = creditsLeft != null && creditsLeft <= cost + 2;
    if (cost >= 4 || lowBalance) {
      setConfirm({ title: title || 'Run this action?', cost, onRun: run });
    } else {
      run && run();
    }
  }, [costs, creditsLeft]);

  const value = { costOf, spend, refreshBalance, creditsLeft };

  return (
    <CreditSpendContext.Provider value={value}>
      {children}
      {confirm && (
        <div className="modal-overlay cost-confirm-overlay" onClick={() => setConfirm(null)}>
          <div className="modal-content cost-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">
                <span aria-hidden="true" style={{ marginRight: 8 }}>⟡</span>{confirm.title}
              </span>
            </div>
            <div className="modal-body">
              <div className="cost-confirm-row">
                <span className="cost-confirm-spend">{confirm.cost} <small>credit{confirm.cost !== 1 ? 's' : ''}</small></span>
                <span className="cost-confirm-after">
                  After this action: <b>{creditsLeft != null ? Math.max(0, creditsLeft - confirm.cost) : '—'} left</b>
                </span>
              </div>
              <p className="cost-confirm-note">This is a credit-spending action. Viewing your results afterward is always free.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary btn-sm" onClick={() => setConfirm(null)}>Cancel</button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => { const fn = confirm.onRun; setConfirm(null); fn && fn(); }}
              >
                Confirm &amp; run
              </button>
            </div>
          </div>
        </div>
      )}
    </CreditSpendContext.Provider>
  );
}
